import { useEffect, useState, useRef } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import FacetedSearchLinkFieldInput from '@Components/Shared/FacetedSearch/FacetedSearchLinkFieldInput';
import FacetedSearchCheckboxFieldInput from '@Components/Shared/FacetedSearch/FacetedSearchCheckboxFieldInput';
import Modal from '@Components/Shared/Modals/Modal';
import { buildNestedTree, applyFacet } from '@Components/Shared/FacetedSearch/FacetedSearchReducer';
import { VariableSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { useWindowSize } from '@Components/Utilities/WindowSize';


const FacetedSearchModalListRow = ({ data, index, style }) => {
    const { virtualizedItems, windowSize, setSize, renderCheckBox } = data;

    let item = virtualizedItems[index];
    const root = React.useRef();
    useEffect(() => {
        if (root.current) {
            let height = Math.max(root.current.children[0]?.scrollHeight, root.current.children[1]?.scrollHeight ?? 0) ?? root.current.scrollHeight;
            height = windowSize.isMobile ? height + 5 : height;
            setSize(index, height);
        }
    }, [item]);
    return (
        <div>
            {Array.isArray(item) ?
                <div ref={root} className='ts-flexbox' style={style}>{item.map(cb => cb && renderCheckBox(cb, false))}</div> :
                item && ('Checked' in item) ?
                    <div ref={root} style={style}>{renderCheckBox(item, false)}</div> :
                    <div ref={root} style={style}>{item}</div>}
        </div>
    );
};

const FacetedSearchModal = (props) => {

    const windowSize = useWindowSize();
    const longListCount = 14;
    const searchTimer = useRef(null);
    const [nestedTree, setNestedTree] = useState([]);
    const [options, setOptions] = useState([]);
    const [topOptions, setTopOptions] = useState([]);
    const [currentFilter, setCurrentFilter] = useState([]);
    const [applyFilter, setApplyFilter] = useState(false);
    const [isFilterBy, setIsFilterBy] = useState(false);
    const [isAscending, setIsAscending] = useState(true);
    const [searchText, setSearchText] = useState('');
    const listRef = useRef();

    useEffect(function facetedSearchModal() {
        let hierarchy = buildNestedTree(props.Options, optionHasForcedSpecLandingHierarchy());
        if (hierarchy.nestedTree.length > 0) {
            setOptionsInState(hierarchy.options);
            setNestedTree(hierarchy.nestedTree);
            setCurrentFilter(hierarchy.options.filter(option => option.Checked));
            if (props.Options.some(option => option.IsFilterByOption)) {
                setIsFilterBy(true);
                setTopOptions([...props.Options].filter(option => option.IsFilterByOption).map(option => {
                    return { ...option, Checked: false, ReactKey: option.ReactKey + 'FilterBy' };
                }));
            } else if (props.ShowPopularOptions) {
                let popularOptions = [];
                if (!searchText && props.Options.length >= props.IsShortListCount) {
                    popularOptions = props.Options.filter(option => option?.Name?.toUpperCase() !== 'UNKNOWN').slice(0).sort((a, b) => b.Count - a.Count).slice(0, 10);
                }
                setTopOptions(popularOptions);
            }
            else if (props.Options.some(option => option.IsPopularItem)) {
                setTopOptions([...props.Options].filter(option => option.IsPopularItem));
            }
        }
    }, [props.Options]);

    useEffect(function searchDispatcher() {
        if (props.SearchResult) {
            setOptionsInState(props.SearchResult);
        }
        if (props.NestedTree) {
            setNestedTree(props.NestedTree);
        }
    }, [props.NestedTree, props.SearchResult]);

    const setOptionsInState = (newOptions) => {
        const nonPopularItems = newOptions.filter(option => !option.IsPopularItem)
        if (nonPopularItems.some(op => op.IsSpecLandingTerm) && props.SpecLandingTermGroup) {
            let filteredSpecOptions = nonPopularItems.filter(op => !op.IsSpecLandingTerm || props.KeepSpecLandingInGroup);
            let filteredSpecLandingTermOptions = nonPopularItems.filter(op => op.IsSpecLandingTerm && !props.KeepSpecLandingInGroup).sort((a, b) => a.DisplayOrder - b.DisplayOrder);
            setOptions(filteredSpecOptions);
            setTopOptions(filteredSpecLandingTermOptions);
        } else {
            setOptions(nonPopularItems);
        }
    };

    const isSpecLandingTermFacet = () => {
        return !props.KeepSpecLandingInGroup && props.SpecLandingTermGroup && props.Options.some(op => op.IsSpecLandingTerm);
    };

    const optionHasForcedSpecLandingHierarchy = () => {
        return props.KeepSpecLandingInGroup && props.Options.some(op => op.IsSpecLandingTerm)
    }

    const getTopOptionsLabelTranslationKey = (isForAriaLabel) => {
        if (isFilterBy) {
            if (isForAriaLabel) {
                return 'sandhillssystem.filter';
            }
            return 'sandhills.filterby';
        } else if (isSpecLandingTermFacet()) {
            return 'sandhills.group';
        } else {
            return 'sandhills.popular';
        }
    };

    const getSortedOptions = (facets, isAsc) => {
        if (isAsc) {
            return [...facets].sort((a, b) => (a.Name > b.Name) ? 1 : -1);
        } else {
            return [...facets].sort((a, b) => (a.Name < b.Name) ? 1 : -1);
        }
    };

    const getSortIconClass = () => {
        let cssClass = 'fas';
        if (isAscending) {
            cssClass += ' fa-sort-alpha-down';
        } else {
            cssClass += ' fa-sort-alpha-up';
        }
        return cssClass;
    };

    const clickCheckBox = (e, selectedOption) => {
        let isChecked = e.target.checked;
        selectedOption.IsSegmentSelected = false;
        if (selectedOption.IsFilterByOption && selectedOption.ReactKey.indexOf('FilterBy') >= 0) {
            let selectedFilterByOptions = [];
            if (isChecked) {
                if (topOptions.length > 0) {
                    selectedFilterByOptions = topOptions.filter(option => option.Checked);
                }
                selectedFilterByOptions.push(selectedOption);
            } else {
                if (topOptions.length > 0) {
                    selectedFilterByOptions = topOptions.filter(option => option.Checked && option.ReactKey !== selectedOption.ReactKey);
                }
            }
            setTopOptions(previousState => {
                return [...previousState].map(option => {
                    if (option.ReactKey == selectedOption.ReactKey) {
                        return { ...option, Checked: isChecked };
                    } else {
                        return option;
                    }
                });
            });
            props.SearchWorker.postMessage({
                text: searchText,
                nestedTree: nestedTree,
                selectedFilterOptions: selectedFilterByOptions
            });
        } else {
            let optionsForApply = [];
            if (isSpecLandingTermFacet()) {
                optionsForApply = options.concat(topOptions);
            } else {
                optionsForApply = options;
            }
            let result = applyFacet(selectedOption, isChecked, optionsForApply, nestedTree, props.ParentNameForSearch, isSpecLandingTermFacet(), optionHasForcedSpecLandingHierarchy());
            setOptionsInState(result.options);
            setNestedTree(result.nestedTree);
            setCurrentFilter(result.currentFilter);
            setApplyFilter(true);
            if (props.ShowPopularOptions || selectedOption.IsPopularItem) {
                setTopOptions(previousState => {
                    return [...previousState].map(option => {
                        if (option.ReactKey == selectedOption.ReactKey) {
                            return { ...option, Checked: isChecked };
                        } else {
                            return option;
                        }
                    });
                });
            }
        }
    };

    const runSearch = (handleNextUpFacet, nextUpFacet) => {
        if (applyFilter || props.IsNextUp || handleNextUpFacet) {
            let search = [];
            let allAvailableOptions = props.IsSpecGroup ? props.Options.map(option => option.Name.replace(' ', '')) : [];
            let fieldName = props.IsSpecGroup ? 'FacetedSpecGroup' : props.FieldName;
            const selectedCurrentItems = currentFilter.filter(option => !option.IsPopularItem);

            if (props.ParentNameForSearch) {
                let parentOptions = selectedCurrentItems.filter(option => option.IsGroup).map(option => option.Value);
                let parentValues = props.IsSpecGroup ? parentOptions.join('&') : parentOptions.join('|');
                search.push({
                    fieldName: props.ParentNameForSearch,
                    value: parentValues,
                    IsSpecGroup: props.IsSpecGroup,
                    AllAvailableOptions: allAvailableOptions
                });
                let childOptions = selectedCurrentItems.filter(option => !option.IsGroup).map(option => option.Value);
                let childValues = props.IsSpecGroup ? childOptions.join('&') : childOptions.join('|');
                search.push({
                    fieldName: fieldName,
                    value: childValues,
                    IsSpecGroup: props.IsSpecGroup,
                    AllAvailableOptions: allAvailableOptions
                });
            } else if (isSpecLandingTermFacet() || optionHasForcedSpecLandingHierarchy()) {
                let selectedSpecTerms = selectedCurrentItems.filter(option => option.IsSpecLandingTerm).map(option => option.Value);
                let selectedSpecs = selectedCurrentItems.filter(option => !option.IsSpecLandingTerm).map(option => option.Value);
                let specTerms = selectedSpecTerms.join('|');
                let specValues = selectedSpecs.join('|');
                search.push({
                    fieldName: props.SpecLandingTermGroup,
                    value: specTerms,
                    AllAvailableOptions: allAvailableOptions
                });
                search.push({
                    fieldName: fieldName,
                    value: specValues,
                    AllAvailableOptions: allAvailableOptions
                });
            } else {
                let selectedValues = selectedCurrentItems.map(option => option.Value);
                if (props.IsSpecGroup) {
                    selectedValues = selectedValues.map(value => value.replace(' ', '') + '=1');
                }
                let value = props.IsSpecGroup ? selectedValues.join('&') : selectedValues.join('|');
                search.push({
                    fieldName: fieldName,
                    value: value,
                    IsSpecGroup: props.IsSpecGroup,
                    AllAvailableOptions: allAvailableOptions
                });
            }

            if (props.Options.some(option => option.IsPopularItem)) {
                const popularFieldName = props.Options.filter(option => option.IsPopularItem)[0].PopularFieldName;
                const popularValue = currentFilter.filter(option => option.IsPopularItem).map(option => option.Value).join('|');
                search.push({
                    fieldName: popularFieldName,
                    value: popularValue,
                    AllAvailableOptions: allAvailableOptions
                });
            }

            props.RunSearch(search, handleNextUpFacet ? nextUpFacet : '');
        }
        props.CloseModal(applyFilter);
    };

    const toggleSort = () => {
        let isAsc = !isAscending;
        if (options.length > 0) {
            setOptionsInState(getSortedOptions(options, isAsc));
            setNestedTree(getSortedOptions(nestedTree, isAsc));
        }
        setIsAscending(isAsc);
    };

    const onClear = () => {
        setSearchText('');
        props.SearchWorker.postMessage({
            text: '',
            nestedTree: nestedTree,
            filterOptions: isFilterBy ? topOptions : []
        });
    };

    const onSearch = (text) => {
        setSearchText(text);
        sizeMap.current = {};
        listRef?.current?.resetAfterIndex(0);
        if (searchTimer.current) {
            clearTimeout(searchTimer.current);
            searchTimer.current = null;
        }
        searchTimer.current = setTimeout(() => {
            props.SearchWorker.postMessage({
                text: text,
                nestedTree: nestedTree,
                filterOptions: isFilterBy ? topOptions : []
            });
        }, 250);
    };

    const renderCheckBox = (item, isTopOptions) => {
        let key = item.ReactKey + (isTopOptions ? '-top' : '') + '-facet-modal-option';
        if (item.MustBeSelectedIndividually && props.FacetClosed !== true && !item.MustBeSelectedIndividuallySkipAdditionalStyling) {
            return (
                <FacetedSearchLinkFieldInput
                    {...item}
                    key={key}
                    ShowCounts={props.ShowCounts && item.ShowCount}
                    AdditionalClassName={item.Visible ? '' : 'hide'}
                    FieldName={props.FieldName}
                    CheckboxChange={(e) => clickCheckBox(e, item)}
                />
            );
        } else {
            return (
                <FacetedSearchCheckboxFieldInput
                    {...item}
                    key={key}
                    ShowCounts={props.ShowCounts && item.ShowCount}
                    AdditionalClassName={item.Visible ? '' : 'hide'}
                    FieldName={props.FieldName}
                    CheckboxChange={(e) => clickCheckBox(e, item)}
                />
            );
        }
    };

    const isAuctionDate = () => {
        return props.FieldName == "AuctionDate";
    }

    const isLongOptions = () => {
        return props.Options && (props.Options.length >= longListCount);
    };

    const getModalColumnClass = () => {
        return isLongOptions() ? '' : ' single-column';
    };

    const getColumnClass = () => {
        return getTrueOptionsCount() >= longListCount ? ' two-column' : ' one-column';
    };

    const getTrueOptionsCount = () => {
        let totalOptions = options.filter(option => option.Visible).length;
        if (isSpecLandingTermFacet()) {
            totalOptions += topOptions.filter(option => option.Visible).length;
        }
        return totalOptions;
    };

    const renderActionButtons = (isMobile, isTop) => {
        let actionButtons = [];

        if (!isTop) {
            actionButtons.push(<button type='button' className='show-all-apply-filter ts-button' onClick={() => runSearch(false)}>
                <TranslationText tKey='sandhills.applyfilter&close' />
            </button>);
        }

        if (props.UseNextUpFacets && props.NextUpFacets && currentFilter.length > 0) {
            let nufButtons = [];
            props.NextUpFacets.map(nuf => {
                if (isTop) {
                    if (nufButtons.length > 0) {
                        nufButtons.push(<br />);
                    }
                    nufButtons.push(<button className={`nuf-button ${nuf.FieldName}-button ts-button`} onClick={() => runSearch(props.UseNextUpFacets, nuf.FieldName)}>
                        <TranslationText tKey={nuf.TranslationKey} />
                    </button>);
                } else {
                    nufButtons.push(<button type='button' className='show-all-apply-filter ts-button' onClick={() => runSearch(props.UseNextUpFacets, nuf.FieldName)}>
                        <TranslationText tKey={nuf.TranslationKey} />
                    </button>);
                }
            });

            if (!isTop || isMobile) {
                actionButtons.push(<div className="additional-button-row">{nufButtons}</div>);
            }
        }

        return actionButtons;
    };

    /* Virtualized Props. Methods, Helpers, etc.. */
    var numColumns = 2;

    const getVirtualizedItems = () => {
        let items = [];

        if (options.some(option => option.Visible)) {
            if (topOptions.some(option => option.Visible) && ((isLongOptions() && !searchText) || isSpecLandingTermFacet())) {
                items.push(<div>
                    <div role='group' aria-label={translator.translate(getTopOptionsLabelTranslationKey(true))}>
                        <h3 className='faceted-header'>
                            <TranslationText tKey={getTopOptionsLabelTranslationKey()} />
                        </h3>
                        <div className={'option-container two-column'}>
                            {topOptions.map(item => renderCheckBox(item, true))}
                        </div>
                    </div>
                    <hr className='clear' />
                    <div style={{height: '8px'}} />
                </div>);
            }

            if (props.AllowSorting && options.filter(option => option.Visible).length > 1) {
                items.push(<div>
                    <button className='ts-button faceted-option-checkbox-sort' onClick={toggleSort}>
                        <TranslationText tKey='Sandhills.Sort' />
                        <i className={getSortIconClass()}></i>
                    </button>
                </div>);
            }

            if (props.FilterOptionsBySelections) {
                items.push(<div>
                    <button className='ts-button faceted-option-checkbox-sort--high-contrast' onClick={() => props.LoadFullOptionTree(options)}> 
                        {props.FilterOptionsByViewAllLabel || translator.translate("sandhills.tradesites.mvc.viewallcategories")}
                    </button>
                </div>);
            }

            if (topOptions.length > 0 && isLongOptions() && !props.FilterOptionsBySelections && !searchText && options.some(option => option.Visible)) {
                items.push(<div className='faceted-option-checkbox-container faceted-header'>
                    <TranslationText tKey='sandhills.all' />
                </div>);
            }

            const filteredOptions = isAuctionDate() ? options.filter(option => option.Visible && !option.IsFilterByOption) : options.filter(option => option.Visible);
            if (windowSize.isMobile || numColumns == 1 || getTrueOptionsCount() < longListCount) {
                items = items.concat(filteredOptions);
            } else {
                const columns = chunkify(filteredOptions, numColumns, true);
                for (var i = 0; i < columns[0].length; i++) {
                    var rowItems = [];
                    for (var col = 0; col < numColumns; col++) {
                        rowItems.push(columns[col][i]);
                    }

                    items.push(rowItems);
                }

                function chunkify(a, n, balanced) {
                    if (n < 2)
                        return [a];

                    var len = a.length,
                        out = [],
                        i = 0,
                        size;

                    if (len % n === 0) {
                        size = Math.floor(len / n);
                        while (i < len) {
                            out.push(a.slice(i, i += size));
                        }
                    } else if (balanced) {
                        while (i < len) {
                            size = Math.ceil((len - i) / n--);
                            out.push(a.slice(i, i += size));
                        }
                    } else {
                        n--;
                        size = Math.floor(len / n);
                        if (len % size === 0)
                            size--;
                        while (i < size * n) {
                            out.push(a.slice(i, i += size));
                        }
                        out.push(a.slice(size * n));
                    }

                    return out;
                }
            }
        }

        return items;
    };

    const sizeMap = React.useRef({});
    const setSize = (index, size) => {
        sizeMap.current = { ...sizeMap.current, [index]: size };
        listRef.current.resetAfterIndex(index);
    };
    const getSize = (index) => { return sizeMap.current[index] || 22; };
    const iOSHeightOffset = props.MobileSearch ? props.NextUpFacets && currentFilter.length > 0 ? 85 : 75 : 0;
    

    const virtualizedItems = getVirtualizedItems();
    const getItemData = () => {
        return {
            virtualizedItems,
            setSize,
            renderCheckBox,
            windowSize
        };
    };
    const renderList = () => {
        if (options.some(option => option.Visible)) {
            if (isLongOptions() || isSpecLandingTermFacet()) {
                return (
                    <div role='group' aria-label={translator.translate('sandhills.all')} className='option-container' style={{ height: '100%' }}>
                        <AutoSizer>
                            {({ height, width }) => (
                                <VariableSizeList
                                    className="List"
                                    height={height - iOSHeightOffset}
                                    itemCount={virtualizedItems.length}
                                    itemSize={getSize}
                                    itemData={getItemData()}
                                    width={width}
                                    ref={listRef}
                                >
                                    {FacetedSearchModalListRow}
                                </VariableSizeList>
                            )}
                        </AutoSizer>
                    </div>
                );
            } else {
                return (
                    <div role='group' aria-label={translator.translate('sandhills.all')} className='option-container'>
                        {props.FilterOptionsBySelections &&
                            <div>
                                <button className='ts-button faceted-option-checkbox-sort--high-contrast' onClick={() => props.LoadFullOptionTree(options)}> 
                                    {props.FilterOptionsByViewAllLabel || (translator.translate("sandhills.tradesites.mvc.viewallcategories"))}
                                </button>
                            </div>}
                        <div className={'option-container' + getColumnClass()}>
                            {isAuctionDate() ? options.filter(option => !option.IsGroup).map(item => renderCheckBox(item, false)) : options.map(item => renderCheckBox(item, false))}
                        </div>
                    </div>
                );
            }
        } else if (options.length > 0) {
            return (<div className='option-container one-column'>
                <TranslationText tKey='sandhills.noresultsfound' />...
            </div>);
        }
    };

    const exitSearch = () => {
        if (props.IsNextUp) {
            props.RunSearch();
        }

        props.CloseModal(props.IsNextUp, (!props.FilterOptionsBySelections && applyFilter));
    };

    return (
        <Modal containerClassName='show-all-modal' contentClassName={'show-all-modal-content-container ' + getModalColumnClass()} onEscapeKeyClose={exitSearch} onEnterKeySubmit={() => runSearch(false)}>
            {/*Don't remove the following div. HS uses as part of their CSS targeting so we can't remove it*/}
            <div className='show-all-modal-content'>
                {props.ShowLoadingSpinner ?
                    <LoadingRing backdrop /> :
                    <>
                        <Modal.Header headerClassName='show-all-modal-header' onClose={exitSearch}>
                            <div className="top-row">
                                <button className='ts-button back-button' onClick={exitSearch}><TranslationText tKey='Sandhills.Back' /></button>
                                <Modal.Title headingClassName='show-all-modal-title'>
                                    {props.IsFitsTo && translator.translate('sandhills.tradesites.mvc.fitsto') + ' '}{props.DisplayName}
                                </Modal.Title>
                                <button className='ts-button apply-button' onClick={() => runSearch(props.UseNextUpFacets)}><TranslationText tKey='Sandhills.Apply' /></button>
                            </div>
                            {renderActionButtons(props.MobileSearch, true)}
                        </Modal.Header>
                        <Modal.Body>
                            {isLongOptions() &&
                                <div className='filter-options'>
                                    <label className="visually-hidden" htmlFor={props.FieldName + '-filter'}>{translator.translate('sandhillssystem.filter')}</label>
                                    <input
                                        type='text'
                                        placeholder={translator.translate('sandhillssystem.filter')}
                                        id={props.FieldName + '-filter'}
                                        value={searchText}
                                        autoComplete='off'
                                        onChange={(e) => onSearch(e.target.value)}
                                    />
                                    {searchText &&
                                        <div role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); onClear(); } }} className='clear-text' onClick={onClear}>
                                            <TranslationText tKey='sandhills.clear' />
                                        </div>}
                                </div>}
                                <fieldset className="ts__fieldset">
                                <legend className='visually-hidden ts__legend'><TranslationText tKey='sandhills.filteringby' />: {props.DisplayName}</legend>
                                    <div className={'all-options-container' + ((isLongOptions() || isSpecLandingTermFacet()) ? props.NextUpFacets && currentFilter.length > 0 ? ' fixed-search-height-additional' : ' fixed-search-height' : '')}>
                                        {renderList()}
                                    </div>
                                </fieldset>
                            <div>
                                {renderActionButtons(props.MobileSearch, false)}
                            </div>
                        </Modal.Body>
                    </>}
            </div>
        </Modal>
    );
};

export default FacetedSearchModal;